import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarRange,
  Database,
  FlaskConical,
  Info,
  LayoutGrid,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react';
import './analytics.css';
import {
  WIDGET_SIZE_ORDER,
  getWidgetDefinition,
  type WidgetDefinition,
  type WidgetSizeClass,
} from './widgetRegistry.tsx';
import { PageHeader, WidgetFrameProvider } from './controller/shared.tsx';
import { useDashboardWidgetSelection } from '../../hooks/dashboards/useDashboardLayout.ts';
import { useOptionalAuditYear } from '../../contexts/AuditYearContext.tsx';
import { useOrgModuleAccess } from '../../hooks/useOrgModuleAccess.ts';
import { useTesterTesters } from '../../hooks/analytics/useTesterReports.ts';
import { AnalyticsScopeOverride } from '../../hooks/analytics/useAnalyticsScope.tsx';
import { useNoDemoData } from '../../hooks/analytics/useNoDemoData.ts';
import { TENANT_PATHS } from '../../routing/paths.ts';
import { isReportingEnabled } from '../ReportingRoute.tsx';

/**
 * Grid per size band: small = up to 4 per row, medium = 2 per row, wide =
 * full width. The small band narrows to its tile count (1-3) so a short row
 * fills the width instead of leaving a gap. Literal classes for the Tailwind
 * JIT scanner.
 */
const SMALL_BAND_GRID = [
  'grid grid-cols-1 gap-4',
  'grid grid-cols-1 gap-4 sm:grid-cols-2',
  'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3',
  'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4',
];

function bandGridClass(size: WidgetSizeClass, count: number): string {
  if (size === 'small') return SMALL_BAND_GRID[Math.min(Math.max(count, 1), 4) - 1];
  if (size === 'medium') return 'grid grid-cols-1 gap-4 lg:grid-cols-2';
  return 'grid grid-cols-1 gap-4';
}

/** Minimum body height per size, so tiles in a row line up even when one widget renders less. */
const BODY_MIN_HEIGHT: Record<WidgetSizeClass, string> = {
  small: 'min-h-[120px]',
  medium: 'min-h-[340px]',
  wide: 'min-h-[200px]',
};

/** Keeps one widget's render error from blanking the rest of the dashboard. */
class TileErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center">
          <AlertTriangle className="h-5 w-5 text-rose-500" aria-hidden />
          <p className="text-sm text-rose-600">This widget failed to display.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Roles the backend lets view another tester's work (`?assignee_user_id=`). */
const TESTER_PICKER_ROLES = new Set(['org_admin', 'internal_auditor']);

/** Tester picker for tester-scoped tiles; "Me" (empty) keeps the viewer's own work. */
const TesterPicker: React.FC<{ id: string; value: number | null; onChange: (id: number | null) => void }> = ({
  id,
  value,
  onChange,
}) => {
  const { data: testers } = useTesterTesters();
  return (
    <label htmlFor={id} className="flex shrink-0 items-center gap-1.5 text-xs text-slate-500">
      Tester
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-700"
      >
        <option value="">Me</option>
        {(testers ?? []).map((t) => (
          <option key={t.user_id} value={t.user_id}>
            {t.name}
          </option>
        ))}
      </select>
    </label>
  );
};

function newDemoNonce(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`.padEnd(14, '0');
}

/** Live/Demo switch on a tile; shown only when the backend allows demo data. */
const DemoToggle: React.FC<{ isDemo: boolean; onToggle: () => void; title: string }> = ({ isDemo, onToggle, title }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={isDemo}
    aria-label={isDemo ? `Show live data for ${title}` : `Show demo data for ${title}`}
    title={isDemo ? 'Showing demo data. Click for live data.' : 'Showing live data. Click for demo data.'}
    className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 ${
      isDemo ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
    }`}
  >
    {isDemo ? <FlaskConical className="h-3.5 w-3.5" aria-hidden /> : <Database className="h-3.5 w-3.5" aria-hidden />}
    {isDemo ? 'Demo' : 'Live'}
  </button>
);

/**
 * Uniform tile frame: title, info tooltip, then the widget body rendered
 * "bare" inside it. With `demoAvailable`, a Live/Demo switch shows a random
 * stored demo variant instead of live data; every switch to Demo picks a new
 * nonce so a fresh variant loads. Tiles start on Live and are not persisted.
 */
const WidgetTile: React.FC<{ widget: WidgetDefinition; canPickTester: boolean; demoAvailable: boolean }> = ({
  widget,
  canPickTester,
  demoAvailable,
}) => {
  const Component = widget.Component;
  const [assigneeUserId, setAssigneeUserId] = useState<number | null>(null);
  const [demoNonce, setDemoNonce] = useState<string | null>(null);
  const isDemo = demoAvailable && demoNonce !== null;
  const noDemoData = useNoDemoData(isDemo ? demoNonce : null);
  const tooltipId = `widget-tip-${widget.widget_key}`;

  const body = widget.testerScoped ? <Component assigneeUserId={isDemo ? null : assigneeUserId} /> : <Component />;

  return (
    <div
      className={`flex min-w-0 flex-col rounded-xl border bg-white shadow-sm ${
        isDemo ? 'border-dashed border-amber-400' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
        <h3 className="min-w-0 truncate text-sm font-semibold text-slate-900" title={widget.title}>
          {widget.title}
        </h3>
        <div className="flex shrink-0 items-center gap-3">
          {widget.testerScoped && canPickTester && !isDemo ? (
            <TesterPicker id={`tester-pick-${widget.widget_key}`} value={assigneeUserId} onChange={setAssigneeUserId} />
          ) : null}
          {demoAvailable ? (
            <DemoToggle
              isDemo={isDemo}
              title={widget.title}
              onToggle={() => setDemoNonce(isDemo ? null : newDemoNonce())}
            />
          ) : null}
          {widget.description ? (
            <span className="group relative">
              <button
                type="button"
                aria-describedby={tooltipId}
                aria-label={`About ${widget.title}`}
                className="rounded text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <Info className="h-3.5 w-3.5" aria-hidden />
              </button>
              <span
                id={tooltipId}
                role="tooltip"
                className="pointer-events-none absolute right-0 top-5 z-20 hidden w-64 rounded-md bg-slate-900 px-2.5 py-2 text-xs leading-snug text-white shadow-lg group-hover:block group-focus-within:block"
              >
                {widget.description}
              </span>
            </span>
          ) : null}
        </div>
      </div>
      {isDemo ? (
        <p className="border-b border-amber-100 bg-amber-50 px-4 py-1 text-[11px] font-medium text-amber-800">
          Demo data: sample numbers, not your organization&apos;s
        </p>
      ) : null}
      <div className={`flex min-h-0 flex-1 flex-col p-4 ${BODY_MIN_HEIGHT[widget.size]}`}>
        <TileErrorBoundary>
          <WidgetFrameProvider embedded>
            {isDemo ? (
              noDemoData ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center">
                  <FlaskConical className="h-5 w-5 text-amber-500" aria-hidden />
                  <p className="max-w-xs text-sm text-slate-500">
                    No demo data yet. A platform admin can generate it in Platform Settings.
                  </p>
                </div>
              ) : (
                <AnalyticsScopeOverride scope={{ auditYearId: null, dataMode: 'demo', demoNonce: demoNonce ?? '' }}>
                  {body}
                </AnalyticsScopeOverride>
              )
            ) : (
              body
            )}
          </WidgetFrameProvider>
        </TileErrorBoundary>
      </div>
    </div>
  );
};

/**
 * The single reporting dashboard. Shows the widgets the user chose in
 * Settings → Dashboard Widgets, laid out by size band (small, medium, wide)
 * and in the user's order within each band. Every widget reads the audit
 * year selected in the sidebar.
 *
 * `?widget=<widget_key>` focuses a single widget (whether or not the user
 * picked it), so other pages can deep-link to one report.
 */
export const WidgetDashboard: React.FC = () => {
  const selection = useDashboardWidgetSelection();
  const [searchParams] = useSearchParams();
  const focusKey = searchParams.get('widget');
  const focused = focusKey ? getWidgetDefinition(focusKey) : undefined;
  const auditYear = useOptionalAuditYear();
  const { data: moduleAccess } = useOrgModuleAccess();
  const canPickTester = TESTER_PICKER_ROLES.has(moduleAccess?.role ?? '');
  const reportingOn = isReportingEnabled(moduleAccess);
  const demoAvailable = Boolean(moduleAccess?.widgets_demo_data);
  const queryClient = useQueryClient();
  const fetchingCount =
    useIsFetching({ queryKey: ['analytics'] }) + useIsFetching({ queryKey: ['pbc'] });
  const isFetching = fetchingCount > 0;

  // "Refreshed" is when the last round of widget fetches finished, not when
  // the button was clicked.
  const [refreshedAt, setRefreshedAt] = useState<Date>(() => new Date());
  const wasFetching = useRef(false);
  useEffect(() => {
    if (wasFetching.current && !isFetching) setRefreshedAt(new Date());
    wasFetching.current = isFetching;
  }, [isFetching]);

  const bands = useMemo(() => {
    const grouped: Record<WidgetSizeClass, WidgetDefinition[]> = { small: [], medium: [], wide: [] };
    if (focused) {
      grouped[focused.size].push(focused);
      return grouped;
    }
    for (const key of selection.widgetKeys) {
      const def = getWidgetDefinition(key);
      if (def) grouped[def.size].push(def);
    }
    return grouped;
  }, [selection.widgetKeys, focused]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
    queryClient.invalidateQueries({ queryKey: ['pbc'] });
  };

  // The stored year id is applied to requests before the year list loads, so
  // don't claim "All years" while the name is still unknown.
  const yearLabel =
    auditYear?.selectedAuditYear?.name ?? (auditYear?.selectedAuditYearId ? 'Loading…' : 'All years');
  const widgetCount = focused ? 1 : selection.widgetKeys.length;

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<LayoutGrid className="h-5 w-5" />}
          iconClassName="bg-teal-50 text-teal-600"
          title="Dashboard"
          description={
            focused
              ? `Showing one widget: ${focused.title}`
              : selection.data
              ? `${widgetCount} widget${widgetCount === 1 ? '' : 's'}${selection.isDefault ? ' (starter set)' : ''} · refreshed ${refreshedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
              : undefined
          }
          actions={
            <>
              <span
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600"
                title="Change the audit year from the selector in the sidebar"
              >
                <CalendarRange className="h-4 w-4 text-slate-400" aria-hidden />
                Audit year: <span className="font-medium text-slate-900">{yearLabel}</span>
              </span>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isFetching}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
                Refresh
              </button>
              {focused && !reportingOn ? null : focused ? (
                <Link
                  to={TENANT_PATHS.ANALYTICS_DASHBOARD}
                  className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Full dashboard
                </Link>
              ) : (
                <Link
                  to={TENANT_PATHS.SETTINGS_DASHBOARD_WIDGETS}
                  className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700"
                >
                  <SlidersHorizontal className="h-4 w-4" aria-hidden />
                  Choose widgets
                </Link>
              )}
            </>
          }
        />

        {selection.isLoading && !focused ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="analytics-skeleton mb-4 h-4 w-1/3" />
                <div className="analytics-skeleton h-32 w-full" />
              </div>
            ))}
          </div>
        ) : selection.isError && !focused ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-6 text-center text-sm text-rose-600">
            Your dashboard widgets could not be loaded. Refresh the page to try again.
          </div>
        ) : widgetCount === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50">
              <LayoutGrid className="h-7 w-7 text-teal-500" aria-hidden />
            </div>
            <p className="mb-1 text-base font-semibold text-slate-700">No widgets on your dashboard</p>
            <p className="mb-5 text-sm text-slate-400">Pick the reports you want to see in Settings.</p>
            <Link
              to={TENANT_PATHS.SETTINGS_DASHBOARD_WIDGETS}
              className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700"
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden />
              Choose widgets
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {WIDGET_SIZE_ORDER.map((size) =>
              bands[size].length > 0 ? (
                <div key={size} className={bandGridClass(size, bands[size].length)}>
                  {bands[size].map((widget) => (
                    <WidgetTile
                      key={widget.widget_key}
                      widget={widget}
                      canPickTester={canPickTester}
                      demoAvailable={demoAvailable}
                    />
                  ))}
                </div>
              ) : null
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetDashboard;
